import { Dropdown } from "antd";
import React, { useEffect, useState } from "react";
import Button from "style-guide/component/Button";
import Modal from "style-guide/component/Modal";
import Typography from "style-guide/component/Typography";
import { MoreOutlined } from "@ant-design/icons";
import { useAccessControl } from "hooks/useAccessControl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "redux/store";
import {
  cancelSubscription,
  getPlanDetails,
} from "../../../redux/slice/rfpModuleSlice/subscription";
import DemoRequestModal from "../../pricing/DemoRequestModal";
import { PricingPlan } from "types/subscription";

// Shimmer component for loading state
const PlanCardSkeleton = () => (
  <div className="relative p-6 bg-gray-50 rounded-xl shadow-md flex flex-col h-full border animate-pulse">
    {/* Plan Title Skeleton */}
    <div className="h-[60px] flex items-start mb-4">
      <div className="w-3/4 h-6 bg-gray-300 rounded" />
    </div>

    {/* Plan Description Skeleton */}
    <div className="h-[40px] flex items-start mb-6">
      <div className="w-full h-4 bg-gray-300 rounded" />
    </div>

    {/* Price Section Skeleton */}
    <div className="h-[60px] flex items-start mb-6">
      <div className="w-1/2 h-8 bg-gray-300 rounded" />
    </div>

    {/* Button Skeleton */}
    <div className="mt-auto">
      <div className="w-full h-10 bg-gray-300 rounded" />
    </div>
  </div>
);

const PlanCards: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"demo" | "sales">("demo");
  const [isCancelling, setIsCancelling] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  const { pricingPlan } = useSelector(
    (state: RootState) => state.subscriptionData
  );

  const { userProfile } = useSelector(
    (state: RootState) => state.applicationData
  );

  const { userTier } = useAccessControl();
  const isBuyCreditProfessional = userProfile?.userDetails[0]?.isBuyCreditProfessional;

  useEffect(() => {
    const fetchPlanDetails = async () => {
      setIsLoadingPlans(true);
      try {
        await dispatch(getPlanDetails());
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlanDetails();
  }, [dispatch]);

  const getButtonText = (plan: string) => {
    if (plan.toLowerCase() === "starter") {
      return "Get Started";
    }
    if (plan.toLowerCase() === "teams") {
      return "Request Demo";
    }
    if (plan.toLowerCase() === "public sector and enterprises") {
      return "Talk to Sales";
    }
    return "Upgrade Plan";
  };

  const handlePlanClick = (plan: PricingPlan) => {
    if ((plan.name === userTier && !(plan.name === "Professional" && isBuyCreditProfessional)) || plan.name === "Starter") {
      return;
    }

    // Handle modal-based plans
    if (getButtonText(plan.name) === "Request Demo") {
      setModalType("demo");
      setIsModalOpen(true);
      return;
    }

    if (getButtonText(plan.name) === "Talk to Sales") {
      setModalType("sales");
      setIsModalOpen(true);
      return;
    }

    // Handle checkout plans
    navigate("/checkout", {
      state: {
        planDetails: plan,
      },
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmCancel = async () => {
    setIsCancelling(true);
    try {
      const status = await dispatch(cancelSubscription());
      if (status === 200) {
        window.location.reload();
      }
    } finally {
      setIsCancelling(false);
      setCancelModalOpen(false);
    }
  };

  if (isLoadingPlans) {
    return (
      <section className="py-10">
        <div className="flex items-center justify-between mb-6">
          <Typography variant="Heading 4" className="text-gray-900 font-semibold">
            Pricing Plans
          </Typography>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <PlanCardSkeleton />
          <PlanCardSkeleton />
          <PlanCardSkeleton />
          <PlanCardSkeleton />
        </div>
      </section>
    );
  }

  return (
    <section className="py-10">
      <div className="flex items-center justify-between mb-6">
        <Typography variant="Heading 4" className="text-gray-900 font-semibold">
          Pricing Plans
        </Typography>
        {userTier && userTier === "Professional" && (
          <Dropdown
            menu={{
              items: [
                {
                  key: "cancel",
                  label: (
                    <span className="text-red-500">Cancel Subscription</span>
                  ),
                  onClick: () => setCancelModalOpen(true),
                },
              ],
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <MoreOutlined className="text-gray-600 text-lg cursor-pointer" />
          </Dropdown>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {pricingPlan.data?.map((plan) => (
          <div
            key={plan.name}
            className="relative p-6 bg-gray-50 rounded-xl shadow-md flex flex-col h-full border hover:border-blue-primary"
          >
            {/* Plan Title - Fixed height */}
            <div className="h-[60px] flex items-start mb-4">
              <Typography
                variant="Heading 3"
                strong
                className="text-gray-900 leading-tight"
              >
                {plan.name}
              </Typography>
            </div>

            {/* Plan Description - Fixed height to accommodate varying content */}
            <div className="h-[40px] flex items-start mb-6">
              <Typography
                variant="SM"
                className="text-gray-500 leading-relaxed"
              >
                {plan.description}
              </Typography>
            </div>

            {/* Price Section - Fixed height */}
            <div className="h-[60px] flex items-start mb-6">
              <Typography
                variant="Heading 2"
                className="text-gray-900 !font-bold"
              >
                {plan.price !== null ? (
                  <>
                    ${plan.price}
                    <span className="text-lg font-normal text-gray-600 ml-2">
                      per month
                    </span>
                  </>
                ) : (
                  <span className="text-gray-900 font-bold">
                    {plan.name === "Starter" ? "Free" : "Custom"}
                  </span>
                )}
              </Typography>
            </div>

            {/* Button - Always at bottom with consistent spacing */}
            <div className="mt-auto">
              {userTier && userTier === plan.name && !(plan.name === "Professional" && isBuyCreditProfessional) ? (
                <Button
                  type="Disabled"
                  className="w-full bg-gray-100 text-gray-500 cursor-not-allowed"
                  buttonText="Current Plan"
                />
              ) : (
                <Button
                  type="Default"
                  className="w-full"
                  onClick={() => handlePlanClick(plan)}
                  buttonText={getButtonText(plan.name)}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Cancel Subscription Modal */}
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        backdropClosable={!isCancelling}
        showCloseIcon={!isCancelling}
        title="Cancel Subscription"
        footer={
          <>
            <Button
              type={isCancelling ? "Disabled" : "Default"}
              onClick={() => setCancelModalOpen(false)}
              buttonText="No"
            />

            <Button
              type="Danger"
              onClick={handleConfirmCancel}
              buttonText="Yes,Cancel"
              className="text-white bg-red-500"
              loading={isCancelling}
            />
          </>
        }
      >
        <Typography variant="Base" className="mb-2">
          Are you sure you want to cancel your subscription?
        </Typography>
      </Modal>

      {/* Demo Request Modal */}
      <DemoRequestModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        modalType={modalType}
      />
    </section>
  );
};

export default PlanCards;
